<template>
  <div>
    <div>
      <h5 class="mt-3">Upload Media</h5>
      <autocomplete-input
        v-if="showPartsSelect"
        :multiple="true"
        isObject
        track-by="_id"
        :path="`/products/${product._id}/parts/all`"
        :optionsV="tempPart.productParts?.length ? [...tempPart.productParts] : []"
        loadOnFocus
        name="Assign to"
        :customLabel="
          (e) =>
            `${e?.partId} - ${e?.colors[0]?.name} ${
              e?.ApparelSize?.label_size ? `(${e?.ApparelSize?.label_size})` : ''
            }`
        "
        v-model="tempPart.productParts"
        @select="(p) => ({})"
        @remove="(p) => ({})"
      />
      <UploadButton
        accept="*"
        :mediaType="['Image', 'Document', 'Video', 'Audio']"
        @changeType="($e) => (selectedType = $e)"
        v-model="medias"
        :artworks="storeArtworks"
        :hasUploadFromLink="true"
        :folder="uploadFolder"
        resize="300"
        :isLogo="isLogoMedia"
        :defaultType="selectedType"
        :compress="tempPart.compress"
        :isResizeToSquare="true"
        :visibleAttached="!tempPart._id"
        :uploadOnly="!!tempPart._id"
        @add="saveMedia($event)"
        @addArtwork="selectImage($event)"
        class="my-2"
      >
        <template slot="settings" slot-scope="props">
          <div class="mb-3" v-if="selectedType === 'Image'">
            <label>Media Class</label>
            <ProductMediaClassTypesMultiselect :media="tempMedia" :hasPreview="true" />
          </div>

          <label>Upload from computer</label>
          <div class="custom-control custom-checkbox mb-2">
            <input v-model="tempPart.compress" class="custom-control-input" :id="`styled-checkbox`" type="checkbox" />
            <label class="custom-control-label" :for="`styled-checkbox`">Compress</label>
          </div>
        </template>
      </UploadButton>

      <div v-if="product.primaryProductId">
        <loading v-if="loading"></loading>
        <autocomplete-input
          :path="`/products/${product.primaryProductId}/media-images?withPart=1&fieldsSearch=partDescription&fieldsSearch=partId&fieldsSearch=color`"
          :name="'Attach existing product media'"
          label="Images"
          loadOnFocus
          :internalSearch="false"
          isObject
          :disabled="loading"
          track-by="_id"
          :outValKey="true"
          v-model="newImage"
          :options-v="[]"
          @select="selectImage"
          class="w-100"
        >
          <template slot="option" slot-scope="props">
            <PartsDropdownOption
              :selectedPart="{
                partId: props.option.classTypeName,
                ...props.option.productPart[0],
                image: props.option
              }"
              @showPreview="showPreview"
            />
          </template>
          <template slot="singleLabel" slot-scope="props">
            <PartsDropdownOption
              :selectedPart="{
                partId: props.option.classTypeName,
                ...props.option.productPart[0],
                image: props.option
              }"
            />
          </template>
        </autocomplete-input>
      </div>
      <autocomplete-input
        v-else
        :path="`/products/${product._id}/media-images?withPart=1&fieldsSearch=partDescription&fieldsSearch=partId&fieldsSearch=color`"
        :name="'Other Product Images'"
        label="Images"
        loadOnFocus
        isObject
        track-by="_id"
        :internalSearch="false"
        :outValKey="true"
        v-model="newImage"
        :options-v="[]"
        @select="selectImage"
        style="max-width: 400px"
      >
        <template slot="option" slot-scope="props">
          <PartsDropdownOption
            :selectedPart="{
              partId: props.option.classTypeName,
              ...props.option.productPart[0],
              image: props.option
            }"
            @showPreview="showPreview"
            @hidePreview="hidePreview"
          />
        </template>
        <template slot="singleLabel" slot-scope="props">
          <PartsDropdownOption
            :selectedPart="{
              partId: props.option.classTypeName,
              ...props.option.productPart[0],
              image: props.option
            }"
          />
        </template>
      </autocomplete-input>
    </div>
    <div v-if="showOrigin" :style="previewStyle">
      <img class="img-thumbnail mr-2" :style="{ minWidth: '300px' }" :src="previewImage || '/img/loading-img.png'" />
    </div>
  </div>
</template>
<script>
import { mediaHasClassTypeName } from '@/utils/media';
import { getStoreArtworks } from '@/api/stores';
import { createPartMedia, updateMedia, createProductMedia } from '@/api/medias';
import { DECORATED_CLASSS_TYPE } from '@/constants/media';

export default {
  components: {
    ProductMediaClassTypesMultiselect: () => import('../ProductMediaClassTypesMultiselect'),
    UploadButton: () => import('../../../../../components/UploadButton'),
    PartsDropdownOption: () => import('../../../../../components/PartsDropdownOption'),
    PartsDropdown: () => import('../../../../../components/PartsDropdown')
  },
  props: {
    part: Object,
    product: Object,
    showPartsSelect: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      tempMedia: {
        classTypes: [DECORATED_CLASSS_TYPE],
        classTypeId: DECORATED_CLASSS_TYPE.class_type_id,
        classTypeName: DECORATED_CLASSS_TYPE.class_type_name
      },
      selectedType: 'Image',
      newImage: null,
      storeArtworks: [],
      tempPart: this.part ? JSON.parse(JSON.stringify(this.part)) : {},
      showOrigin: false,
      previewImage: null,
      previewPosition: {
        top: 0,
        left: 0
      },
      loading: false
    };
  },
  computed: {
    isLogoMedia() {
      return mediaHasClassTypeName(this.tempMedia, 'Logo');
    },
    medias() {
      return this.tempPart._id && this.tempPart.mediaList;
    },
    uploadFolder() {
      return `${this.product.supplierCode || 'suppliers'}/${this.product.productId || 'products'}/${
        this.tempPart.partId || 'parts'
      }`;
    },
    previewStyle() {
      return {
        position: 'fixed',
        left: this.previewPosition.left,
        top: this.previewPosition.top,
        pointerEvents: 'none',
        zIndex: 10,
        width: '100px',
        height: '100px'
      };
    }
  },
  async mounted() {
    if (this.$route.params.storeId) {
      this.storeArtworks = (await getStoreArtworks(this.$route.params.storeId)).data;
    }
  },
  methods: {
    async selectImage(img) {
      const duplicate = JSON.parse(JSON.stringify(img));
      delete duplicate._id;
      delete duplicate.productPart;
      await this.saveMedia({
        ...duplicate,
        fileLocation: duplicate.fileLocation || duplicate.location
      });
      this.$emit('close');
    },
    async saveMedia(data) {
      try {
        this.loading = true;
        const media = {
          ...data,
          ...this.tempMedia
        };

        const createMedia = async () => {
          console.log('createProductMedia');
          const { _id } = await createProductMedia(this.product._id, media);
          media._id = _id;
          return media;
        };

        if (this.showPartsSelect) {
          if (this.tempPart.productParts?.length) {
            await createMedia();
            await Promise.all(
              this.tempPart.productParts.map(async (part) => {
                this.$emit('onChangePartColors', part, media, 1);
              })
            );
          } else {
            await createMedia();
          }
          this.$emit('reload');
          this.notifySuccess('Media successfully created');
        } else {
          if (!media._id) {
            const { isDefault, _id } = await createPartMedia(media, this.product._id, this.tempPart._id);
            media.isDefault = isDefault;
            media._id = _id;
          } else {
            await updateMedia(media);
          }
          this.$emit('save', media);
        }
      } catch (e) {
        console.log(e);
        this.notifyError(e?.data?.message || e.message || e);
      } finally {
        this.loading = false;
      }
    },
    showPreview(data) {
      const { img, rect } = data;
      this.showOrigin = true;
      this.previewImage = img;
      this.previewPosition = {
        top: `${rect.top - 150}px`,
        left: `${rect.left - 330}px`
      };
    },
    hidePreview() {
      this.showOrigin = false;
      this.previewImage = null;
      this.previewPosition = {
        top: 0,
        left: 0
      };
    }
  }
};
</script>
